import { useContext, useEffect } from 'react';
import { NavigateFunction, Outlet, useNavigate, useLocation } from 'react-router-dom';

import { RegistrationStatusEnum, UserDetail, UserTypeEnum } from '../api';
import { SessionContext } from '../app/contexts/SessionContext';

export const ALL_USER_TYPES = [
  UserTypeEnum.PROVIDER,
  UserTypeEnum.HEALTHCARE_ENTITY_REP,
  UserTypeEnum.STAFF,
];

interface Props {
  allowedUserTypes?: UserTypeEnum[];
  permissionRequired?: string;
}

const handleProviderStatusRedirect = (user: UserDetail, navigate: NavigateFunction): boolean => {
  if (user.user_type !== UserTypeEnum.PROVIDER) {
    return false;
  }

  if (user.registration_status === RegistrationStatusEnum.SETUP) {
    navigate('/register/email-confirmation');
    return true;
  }

  if (user.registration_status === RegistrationStatusEnum.MISSING_INFO) {
    navigate('/register/complete');
    return true;
  }

  return false;
};

export const AuthenticatedRoute = ({ allowedUserTypes, permissionRequired }: Props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isLoading, user, sessionExists } = useContext(SessionContext);

  useEffect(() => {
    if ((!isLoading && !user) || sessionExists === false) {
      const nextPath = encodeURIComponent(location.pathname + location.search);
      return navigate(`/login?next=${nextPath}`);
    }

    if (!!user) {
      if (user.registration_status === RegistrationStatusEnum.REJECTED) {
        return navigate('/');
      }

      if (handleProviderStatusRedirect(user, navigate)) {
        return;
      }

      if (!!allowedUserTypes && !allowedUserTypes.includes(user.user_type)) {
        return navigate('/');
      }

      if (
        permissionRequired &&
        user.user_type !== UserTypeEnum.STAFF &&
        user?.permissions?.includes(permissionRequired) === false
      ) {
        console.log('User does not have permission:', permissionRequired);
        return navigate('/');
      }
    }
  }, [isLoading, user, navigate, allowedUserTypes, location, sessionExists]);

  return <Outlet />;
};
